'use client';

import { Button, CircularProgress, InputAdornment } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useLazyQuery } from '@parkchirp/api-client';
import React, { FC, useCallback, useState } from 'react';
import { GET_AUTO_INVOICE_PREVIEWS } from '../../queries';
import type { SetShowSnackbar } from '../../types';

interface InvoiceReportProps {
	facilityId: string;
	setShowSnackbar: SetShowSnackbar;
}

export const PreviewAutoInvoiceReportForm: FC<InvoiceReportProps> = ({ facilityId, setShowSnackbar }) => {
	const [parkingStartMonth, setParkingStartMonth] = useState<string>();
	const [parkingStartYear, setParkingStartYear] = useState<string>();
	const [parkingEndMonth, setParkingEndMonth] = useState<string>();
	const [parkingEndYear, setParkingEndYear] = useState<string>();
	const [accountId, setAccountId] = useState<string>();
	const [subscriptionName, setSubscriptionName] = useState<string>();
	const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);
	const [getAutoInvoicePreviews] = useLazyQuery(GET_AUTO_INVOICE_PREVIEWS);

	const monthOptions = [
		{ label: 'January', value: '0' },
		{ label: 'February', value: '1' },
		{ label: 'March', value: '2' },
		{ label: 'April', value: '3' },
		{ label: 'May', value: '4' },
		{ label: 'June', value: '5' },
		{ label: 'July', value: '6' },
		{ label: 'August', value: '7' },
		{ label: 'September', value: '8' },
		{ label: 'October', value: '9' },
		{ label: 'November', value: '10' },
		{ label: 'December', value: '11' },
	];

	const getYearOptions = useCallback(() => {
		const startYear = 2021;
		const endYear = new Date().getFullYear() + 5;
		const yearOptions = [];
		for (let i = startYear; i <= endYear; i++) {
			yearOptions.push({ label: i.toString(), value: i });
		}
		return yearOptions;
	}, []);

	const getDownloadParams = useCallback(() => {
		const baseParams: {
			facilityId: string;
			subscriptionName?: string;
			accountId?: string;
		} = {
			facilityId,
			...(accountId && { accountId }),
			...(subscriptionName && { subscriptionName }),
		};

		const dateParams: {
			parkingStartMonth?: string;
			parkingStartYear?: string;
			parkingEndMonth?: string;
			parkingEndYear?: string;
		} = {};

		if (parkingStartMonth && parkingStartYear) {
			dateParams.parkingStartMonth = parkingStartMonth;
			dateParams.parkingStartYear = parkingStartYear.toString();
		}
		if (parkingEndMonth && parkingEndYear) {
			dateParams.parkingEndMonth = parkingEndMonth;
			dateParams.parkingEndYear = parkingEndYear.toString();
		}

		return { ...baseParams, ...dateParams };
	}, [facilityId, parkingStartMonth, parkingStartYear, parkingEndMonth, parkingEndYear, accountId, subscriptionName]);

	const params = getDownloadParams();

	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: 'Parking Period Start Month and Year are required before generating the report.',
				failingCondition: !(parkingStartYear && parkingStartMonth),
			},
			{
				message: ` Parking start time should be before parking end time.`,
				failingCondition:
					parkingStartYear &&
					parkingEndYear &&
					(parkingStartYear > parkingEndYear ||
						(parkingStartYear === parkingEndYear && Number(parkingStartMonth) > Number(parkingEndMonth))),
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [parkingStartMonth, parkingStartYear, parkingEndMonth, parkingEndYear, setShowSnackbar]);

	const handleReset = useCallback(() => {
		setParkingStartMonth('');
		setParkingStartYear('');
		setParkingEndMonth('');
		setParkingEndYear('');
		setAccountId('');
		setSubscriptionName('');
	}, []);

	const handleDownload = useCallback(async () => {
		if (!handleValidation()) {
			return;
		}

		setIsDownloadingCsv(true);
		const { error } = await getAutoInvoicePreviews({
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			variables: params,
			onCompleted: (data: { getAutoInvoicePreviews: { url: string } }) => {
				const csv = document.createElement('a');
				csv.href = data.getAutoInvoicePreviews.url;
				csv.click();
			},
		});
		if (error) {
			setShowSnackbar({
				message: error.message,
				severity: 'error',
				onClose: () => {
					setShowSnackbar(null);
				},
			});
		}
		setIsDownloadingCsv(false);
	}, [getAutoInvoicePreviews, handleValidation, params]);

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2}>
				<Grid item xs={6}>
					<Grid container spacing={1}>
						<Grid item xs={8}>
							<FormControl fullWidth variant='outlined'>
								<InputLabel>Parking Period Start Month</InputLabel>
								<Select
									label='Parking Period Start Month'
									value={parkingStartMonth || ''}
									onChange={(e) => setParkingStartMonth(e.target.value)}
									data-cy='parking-period-start-select-menu'
								>
									{monthOptions.map((month) => (
										<MenuItem key={month.value} value={month.value}>
											{month.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={4}>
							<FormControl fullWidth variant='outlined'>
								<InputLabel>Year</InputLabel>
								<Select
									label='Year'
									value={parkingStartYear || ''}
									onChange={(e) => setParkingStartYear(e.target.value)}
									data-cy='start-year-select-menu'
								>
									{getYearOptions().map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={6}>
					<Grid container spacing={1}>
						<Grid item xs={8}>
							<FormControl fullWidth variant='outlined'>
								<InputLabel>Parking Period End Month</InputLabel>
								<Select
									label='Parking Period End Month'
									value={parkingEndMonth || ''}
									onChange={(e) => setParkingEndMonth(e.target.value)}
									data-cy='parking-period-end-select-menu'
								>
									{monthOptions.map((month) => (
										<MenuItem key={month.value} value={month.value}>
											{month.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={4}>
							<FormControl fullWidth variant='outlined'>
								<InputLabel>Year</InputLabel>
								<Select
									label='Year'
									value={parkingEndYear || ''}
									onChange={(e) => setParkingEndYear(e.target.value)}
									data-cy='end-year-select-menu'
								>
									{getYearOptions().map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={6}>
					<Grid container spacing={1}>
						<Grid item xs={6}>
							<TextField
								type='number'
								fullWidth
								variant='outlined'
								label='Account ID'
								value={accountId || ''}
								onChange={(e) => setAccountId(e.target.value)}
								inputProps={{ 'data-cy': 'account-id-input' }}
								onKeyDown={(event) => {
									if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
										event.preventDefault();
									}
								}}
								onPaste={(event) => {
									event.preventDefault();

									const paste = event.clipboardData.getData('text');
									const cleanedPaste = /^[AC]*[-]*(\d+)$/.exec(paste);

									if (cleanedPaste) {
										setAccountId(cleanedPaste[1]);
									}
								}}
								InputProps={{
									startAdornment: <InputAdornment position='start'>AC-</InputAdornment>,
								}}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								fullWidth
								variant='outlined'
								label='Sub Name'
								value={subscriptionName || ''}
								onChange={(e) => setSubscriptionName(e.target.value)}
								inputProps={{ 'data-cy': 'sub-name-input' }}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'center', alignSelf: 'center', mt: 3 }}>
				<Grid item>
					<Button
						variant='contained'
						size='small'
						onClick={handleDownload}
						disabled={!facilityId || isDownloadingCsv}
						data-cy='download-csv-button'
					>
						{isDownloadingCsv ? <CircularProgress size={24} /> : 'Download CSV'}
					</Button>
				</Grid>
				<Grid item>
					<Button
						variant='contained'
						size='small'
						fullWidth
						color='secondary'
						onClick={handleReset}
						data-cy='reset-button'
					>
						Reset
					</Button>
				</Grid>
			</Grid>
		</Box>
	);
};
